import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import { APP_CONFIG } from "../app-config";
import { encryptDataHybrid, apiCall } from "../utils/api";

const COLORS = APP_CONFIG.colors;

const SessionLogin = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // Get parameters from URL
    const encryptedData = searchParams.get("session_data");
    const aesKey = searchParams.get("aes_key");

    // Decrypt session data
    const getSessionData = () => {
        if (!encryptedData || !aesKey) {
            setError("Missing session data or encryption key");
            setLoading(false);
            return null;
        }

        try {
            const aesKeyWord = CryptoJS.enc.Utf8.parse(aesKey);
            const bytes = CryptoJS.AES.decrypt(encryptedData, aesKeyWord, {
                mode: CryptoJS.mode.ECB,
                padding: CryptoJS.pad.Pkcs7,
            });

            const decrypted = bytes.toString(CryptoJS.enc.Utf8);

            if (!decrypted) {
                throw new Error("Decryption failed");
            }

            const sessionData = JSON.parse(decrypted);

            // Validate required fields
            if (!sessionData.user_id || !sessionData.login_token) {
                throw new Error("Missing user_id or login_token");
            }

            return sessionData;
        } catch (error) {
            console.error("Decryption error:", error);
            setError(error.message || "Invalid session data");
            setLoading(false);
            return null;
        }
    };

    useEffect(() => {
        const sessionData = getSessionData();

        if (!sessionData) {
            return; // Error already set
        }

        const fetchProfile = async () => {
            try {
                // Prepare payload
                const payload = {
                    appPlatform: '',
                    appVersion: '',
                    buildNumber: '',
                    user_id: sessionData.user_id,
                    token: sessionData.login_token,
                    api: 'profile-fetch',
                    payload: {},
                };

                // Encrypt the payload
                const encrypted = encryptDataHybrid(payload);
                if (!encrypted) throw new Error("Encryption failed");

                // Make API request
                const timestamp = Math.floor(Date.now() / 1000);
                const response = await fetch(`${process.env.REACT_APP_API_URL}api.php`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${timestamp * 784116}`,
                    },
                    body: JSON.stringify({
                        encryptedData: encrypted.encryptedData,
                        encryptedKey: encrypted.encryptedKey,
                    }),
                });

                if (!response.ok) throw new Error("Server error");

                const result = await response.json();

                if (result.status === true) {
                    console.log(result.profile);
                    localStorage.setItem("credentials", JSON.stringify(result.profile));

                    if (sessionData?.redirect) {
                        navigate(sessionData?.redirect, { replace: true });
                        return;
                    }
                    navigate("/home", { replace: true });
                } else {
                    setError(result.message || "Failed to fetch profile");
                }
            } catch (error) {
                setError(error.message || "An error occurred");
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []); // Empty dependency array - runs once on mount

    // Show loading spinner
    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div
                    className="w-10 h-10 rounded-full border-4 border-gray-200 animate-spin"
                    style={{ borderTopColor: COLORS.primaryColor }}
                />
            </div>
        );
    }

    // Show error message
    if (error) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="bg-red-50 p-6 rounded-lg max-w-md">
                    <h2 className="text-red-600 text-xl font-bold mb-2">Error</h2>
                    <p className="text-red-700">{error}</p>
                </div>
            </div>
        );
    }

    // Fallback (should never reach here)
    return null;
};

export default SessionLogin;